import React from 'react';
import classNames from 'classnames';

import { AnimatedCheckmark } from './AnimatedCheckmark';

interface PillProps {
  text: string;
  logo?: string;
  /** If true, skip animation — show final state immediately */
  immediate?: boolean;
  /** Abort signal to cancel the typing animation */
  signal?: AbortSignal;
  /** Called when pill is fully done animating */
  onComplete?: () => void;
}

export const Pill: React.FC<PillProps> = (props) => {
  const { text, logo, immediate = false, signal, onComplete } = props;

  const [displayedText, setDisplayedText] = React.useState<string>(immediate ? text : '');
  const [isLoading, setIsLoading] = React.useState<boolean>(!immediate);
  const [isDone, setIsDone] = React.useState<boolean>(immediate);
  const mountedRef = React.useRef<boolean>(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (immediate) return;

    let cancelled = false;
    const abortHandler = () => {
      cancelled = true;
    };
    signal?.addEventListener('abort', abortHandler);

    const run = async () => {
      // Wait before starting typing
      await new Promise<void>((r) => setTimeout(r, 288));
      if (cancelled || !mountedRef.current) return;

      // Type characters one by one
      for (let i = 0; i < text.length; i++) {
        if (cancelled || !mountedRef.current) return;
        setDisplayedText(text.slice(0, i + 1));
        await new Promise<void>((r) => setTimeout(r, 23));
      }

      if (cancelled || !mountedRef.current) return;

      // Mark as done
      setIsLoading(false);
      setIsDone(true);

      await new Promise<void>((r) => setTimeout(r, 115));
      if (!cancelled && mountedRef.current) {
        onComplete?.();
      }
    };

    run();

    return () => {
      cancelled = true;
      signal?.removeEventListener('abort', abortHandler);
    };
  }, [immediate, text, signal, onComplete]);

  return (
    <div className={'pill'}>
      {/* Inner background (replaces ::after) */}
      <div className={'inner-bg'} />

      {/* Rotating border (replaces ::before, only when loading) */}
      <div className={classNames({ 'rotating-border': isLoading, 'fixed-border': !isLoading })} />

      {/* Logo */}
      {logo && (
        <div className={'logo'}>{logo.startsWith('https') ? <img src={logo} alt={text} /> : <span>{logo}</span>}</div>
      )}

      {/* Text */}
      <span className={'text'}>{displayedText}</span>

      {/* Checkmark */}
      {isDone && (
        <div className={'check-wrap'}>
          <AnimatedCheckmark animated={!immediate} />
        </div>
      )}
    </div>
  );
};
